import React, { useEffect, useRef, useState } from 'react';

import { observer } from 'mobx-react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Task, Vehicle } from '@bringg/types';
import { Table, BringgInput, Empty, Tooltip } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { VehicleGroup } from '@bringg/dashboard-sdk/dist/Vehicle/Vehicle.consts';

import { useStores } from '../../../recipes';
import Driver from '../../../stores/drivers/domain-object/driver';
import { ModalParams } from './vehicle-assigner-modal-const';

import './vehicle-list.scss';

export type SelectedVehicleData = {
	id: number;
	available: boolean;
	name: string;
	isSkillsMatch: boolean;
	vehicle_type_name: string;
	max_total_weight: number;
	vehicle_type_id: number;
};

interface Translation {
	labelTextSearchInput: string;
	name: string;
	type: string;
	skills: string;
	capacity: string;
}

interface Props {
	modalParams: ModalParams;
	translations: Translation;
	isTrailer: boolean;
	selectedItemId: number | undefined;
	setSelectedItem: (vehicle: SelectedVehicleData) => void;
	isLoading: boolean;
	disabled?: boolean;
	maxHeight?: number;
	matchSkillsWith?: Partial<SelectedVehicleData> | null;
}

const VehicleTrailerList: React.FC<Props> = ({
	modalParams,
	isTrailer,
	selectedItemId,
	setSelectedItem,
	isLoading,
	translations,
	disabled,
	maxHeight,
	matchSkillsWith
}: Props) => {
	const { t } = useTranslation();
	const { userId, tasks, teamIds, vehicleId, trailerId } = modalParams.data;
	const { driversStore, vehicleTypesStore, vehiclesStore } = useStores();
	const [vehicles, setVehicles] = useState<SelectedVehicleData[] | null>(null);
	const [searchedVehicles, setSearchedVehicles] = useState<SelectedVehicleData[]>([]);
	const [vehiclesWithMissedSkills] = useState(new Map());
	const [search, setSearch] = useState<string>();
	const finishedLoadingForFirstTime = useRef(true);

	useEffect(() => {
		if (!isLoading) {
			let selectedVehiclesData: SelectedVehicleData[] = [];
			const driver: Driver = driversStore.get(userId);

			const teamVehiclesByIsTrailer: Vehicle[] = vehiclesStore
				.getGroup(VehicleGroup.Team, teamIds[0])
				.filter(vehicle => vehicle.is_trailer === isTrailer);

			selectedVehiclesData = convertToSelectedVehicleData(teamVehiclesByIsTrailer, driver?.skills || []).sort(
				vehicleComparer
			);

			if (finishedLoadingForFirstTime) {
				setSelectedItem(
					selectedVehiclesData.find(vehicle => vehicle.id === (isTrailer ? trailerId : vehicleId))
				);
				finishedLoadingForFirstTime.current = false;
			}
			setVehicles(selectedVehiclesData);
			setSearchedVehicles(selectedVehiclesData);
		}
	}, [isLoading, matchSkillsWith]);

	const convertToSelectedVehicleData = (teamVehicles: Vehicle[], driverSkills: string[]) => {
		return teamVehicles.map(vehicle => {
			const { id, name, skills: vehicleSkills, available, vehicle_type_id } = vehicle;
			const vehicleType = vehicleTypesStore.get(vehicle.vehicle_type_id);
			const vehicleSkillsToMatch = vehiclesStore.get(matchSkillsWith?.id)?.skills || [];
			const skillsToMatch = new Set([...(vehicleSkills || []), ...driverSkills, ...vehicleSkillsToMatch]);
			const uniqTasksSkills = collectAllTasksSkillsUniq(tasks);

			return {
				id,
				name,
				available,
				vehicle_type_id,
				isSkillsMatch: matchSkillsWithTasks(uniqTasksSkills, skillsToMatch, id),
				vehicle_type_name: vehicleType?.title,
				max_total_weight: vehicleType?.max_total_weight
			};
		});
	};

	const vehicleComparer = (vehicleA, vehicleB) => {
		if (vehicleA.isSkillsMatch && !vehicleB.isSkillsMatch) return -1;
		if (!vehicleA.isSkillsMatch && vehicleB.isSkillsMatch) return 1;

		if (vehicleA.available && !vehicleB.available) return -1;
		if (!vehicleA.available && vehicleB.available) return 1;

		return vehicleA.name.toString().localeCompare(vehicleB.name.toString());
	};

	const matchSkillsWithTasks = (taskSkills: Set<string>, skills: Set<string>, vehicleID: number): boolean => {
		let skillsUniq = vehiclesWithMissedSkills.get(vehicleID);

		if (!skillsUniq) {
			skillsUniq = new Set();

			taskSkills.forEach(skill => {
				if (!skills.has(skill)) {
					skillsUniq.add(skill);
				}
			});

			vehiclesWithMissedSkills.set(vehicleID, skillsUniq);
		}

		return skillsUniq.size === 0;
	};

	const collectAllTasksSkillsUniq = (tasks: Task[]) => {
		const allTasksSkills = [];

		tasks.forEach(task => {
			const taskSkills = task.required_skills;
			allTasksSkills.push(taskSkills);
		});

		return new Set([...allTasksSkills.flat()]);
	};

	const columns = [
		{
			title: translations.name,
			dataIndex: 'name',
			render: (text, record: SelectedVehicleData) => {
				const { available, id } = record;
				const currentVehicle = isTrailer ? trailerId : vehicleId;
				const currentVehicleTranslate = isTrailer
					? ` ${t('RUN_VEHICLE_ASSIGNMENT.CURRENT_TRAILER')}`
					: ` ${t('RUN_VEHICLE_ASSIGNMENT.CURRENT_VEHICLE')}`;

				return (
					<span className="vehicle-name-container" data-test-id="vehicle-name">
						{id && (
							<Tooltip
								key="on-shift-indication-tooltip"
								title={
									available
										? t('RUN_VEHICLE_ASSIGNMENT.AVAILABLE')
										: t('RUN_VEHICLE_ASSIGNMENT.UNAVAILABLE')
								}
								placement="top"
								overlayClassName="runs-tooltip"
							>
								<BringgIcon
									iconName={BringgFontIcons.Circle}
									className={classNames('vehicle-circle', { available, unavailable: !available })}
								/>
							</Tooltip>
						)}
						<span className="vehicle-name">{` ${text}`}</span>
						{currentVehicle === id && <span className="current-vehicle">{currentVehicleTranslate}</span>}
					</span>
				);
			}
		},
		{
			title: translations.type,
			dataIndex: 'vehicle_type_name',
			key: 'vehicle_type_name',
			render: (vehicleType: string) => {
				return <span data-test-id="vehicle-type-name">{vehicleType}</span>;
			}
		},
		{
			title: translations.skills,
			dataIndex: 'isSkillsMatch',
			render: (isSkillsMatch: boolean, record: SelectedVehicleData) => {
				const tooltipTitle = isSkillsMatch
					? t('RUN_USER_ASSIGNMENT.SKILLS_MATCH')
					: `${t('RUN_USER_ASSIGNMENT.SKILLS_NOT_MATCH')} : ${[
							...vehiclesWithMissedSkills.get(record.id)
					  ].join(', ')}`;
				return (
					<span className="center-field vehicle-skills-icons" data-test-id="vehicle-skills-match">
						{record.id && (
							<Tooltip
								key="vehicle-skills-match-tooltip"
								title={tooltipTitle}
								placement="top"
								className="vehicle-skills-match-tooltip"
							>
								<BringgIcon
									iconName={isSkillsMatch ? BringgFontIcons.Selected : BringgFontIcons.Close}
									className={isSkillsMatch ? 'green-text' : 'red-text'}
								/>
							</Tooltip>
						)}
					</span>
				);
			}
		},
		{
			title: translations.capacity,
			dataIndex: 'max_total_weight',
			render: (capacity: number) => (
				<span className="center-field" data-test-id="vehicle-max-total-weight">
					{capacity || t('NOT_AVAILABLE')}
				</span>
			)
		}
	];

	const searchVehicle = event => {
		const foundVehicles = event.target.value
			? vehicles.filter(vehicle => vehicle.name.toLowerCase().indexOf(event.target.value.toLowerCase()) > -1)
			: vehicles;

		setSearchedVehicles(foundVehicles);
		setSearch(event.target.value);
	};

	const openDisabledMessage = () => {
		return (
			<div className="disabled-message">
				<BringgIcon iconName={BringgFontIcons.Warning} className="disabled-message-icon" />
				<p className="disabled-message-content">{t('RUN_TRAILER_ASSIGNMENT.DISABLED_DESCRIPTION')}</p>
			</div>
		);
	};
	return (
		<div
			className={classNames('vehicles-container', { 'not-allowed': disabled })}
			data-test-id={isTrailer ? 'trailers-container' : 'vehicles-container'}
		>
			{disabled && openDisabledMessage()}
			<div className={classNames({ 'disabled-table': disabled })}>
				<div className="vehicles-container-header">
					<BringgInput
						className="search-row-input"
						disabled={!vehicles}
						placeholder={translations.labelTextSearchInput}
						value={search}
						onChange={searchVehicle}
						suffix={<BringgIcon iconName={BringgFontIcons.Search} />}
						allowClear
					/>
				</div>
				<Table
					rowKey="id"
					columns={columns}
					tableLayout="fixed"
					dataSource={searchedVehicles}
					loading={isLoading}
					scroll={{ y: maxHeight || 150, scrollToFirstRowOnChange: true }}
					rowSelection={{
						selectedRowKeys: [selectedItemId],
						type: 'radio'
					}}
					locale={{ emptyText: <Empty description={''} /> }}
					onRow={record => {
						return {
							onClick: () => setSelectedItem(record)
						};
					}}
				/>
			</div>
		</div>
	);
};

export default observer(VehicleTrailerList);
